import React, { useCallback } from 'react';
import Modal from 'components/Modal';
import { Step } from 'components/SteppedModal';
import { useSocialShareModal } from 'containers/SocialShareModal/SocialShareModalContext';
import useSocialShareConnectedModal from 'containers/SocialShareModal/useSocialShareConnectedModal';
import useOnMount from 'hooks/useOnMount';
import useVerifyYouTubeAccount from 'hooks/useVerifyYouTubeAccount';

import { YouTubeSocialShareStep } from '../../types';
import YouTubeVerificationStep from './YouTubeVerificationStep';

export interface UseYouTubeVerificationStepConfig {
  onAuthSuccess: () => void;
}

export default function useYouTubeVerificationStep({
  onAuthSuccess,
}: UseYouTubeVerificationStepConfig): Step<YouTubeSocialShareStep> {
  const { params } = useSocialShareConnectedModal();

  const { goToStep } = useSocialShareModal();

  const { isVerifying, onVerifyAccount } = useVerifyYouTubeAccount({
    source: 'DownloadPage',
    onAccountVerified: onAuthSuccess,
    onAccountDisallowed: () => goToStep('disallowed-account'),
    onAccountEligible: () => goToStep('elegible-account'),
    onAccountNoChannel: () => goToStep('channel-needed'),
    onAccountVerificationFailure: () => goToStep('access-denied'),
  });

  const handleVerifyAccount = useCallback(() => {
    goToStep('verification-progress');
    onVerifyAccount();
  }, [goToStep, onVerifyAccount]);

  useOnMount(() => {
    if (params?.platform === 'youtube') {
      onVerifyAccount();
    }
  });

  return {
    id: 'verification',
    component: <YouTubeVerificationStep isVerifying={isVerifying} />,
    fluidFooterButtons: true,
    renderFooterButtons: () => [
      <Modal.FooterButton
        size="large"
        theme="submit"
        disabled={isVerifying}
        onClick={handleVerifyAccount}
      >
        my account is now verified
      </Modal.FooterButton>,
    ],
  };
}
